import {SETTING_SPEED_UNITS} from "constants/AppConstants.js";
import ConvertUtil from "utils/ConvertUtil.js";
import * as parser from "fit-file-parser";
import {FIT} from "fit-file-parser/dist/fit.js";
import DirectionUtil from "utils/DirectionUtil.js";
import isNil from "lodash/isNil.js";
import {WATERSPORT_OPTIONS} from "../DropDownUtil.js";
import CoordinateUtil from "utils/CoordinateUtil.js";
import {interpolateAndFilterSpeedFromPoints} from "utils/SpeedUtil.js";

// fit-file-parser is Babel-built CJS: node ESM wraps module.exports one level
// deeper than webpack/babel does, so unwrap `default` until the class surfaces.
const unwrapDefault = (mod: any): any => (typeof mod === "function" ? mod : (mod?.default ?? mod));
const FitParser = unwrapDefault(unwrapDefault(parser));

/* eslint-disable quote-props */
const deviceMapping: Record<string | number, string> = {
  1: "hrm1",
  2: "axh01",
  3: "axb01",
  4: "axb02",
  5: "hrm2ss",
  6: "dsi_alf02",
  7: "hrm3ss",
  8: "hrm_run_single_byte_product_id",
  9: "bsm",
  10: "bcm",
  11: "axs01",
  12: "hrm_tri_single_byte_product_id",
  13: "hrm4_run_single_byte_product_id",
  14: "fr225_single_byte_product_id",
  15: "gen3_bsm_single_byte_product_id",
  16: "gen3_bcm_single_byte_product_id",
  22: "hrm_fit_single_byte_product_id",
  255: "OHR",
  473: "fr301_china",
  474: "fr301_japan",
  475: "fr301_korea",
  494: "fr301_taiwan",
  717: "fr405",
  782: "fr50",
  987: "fr405_japan",
  988: "fr60",
  1011: "dsi_alf01",
  1018: "fr310xt",
  1036: "edge500",
  1124: "fr110",
  1169: "edge800",
  1199: "edge500_taiwan",
  1213: "edge500_japan",
  1253: "chirp",
  1274: "fr110_japan",
  1325: "edge200",
  1328: "fr910xt",
  1333: "edge800_taiwan",
  1334: "edge800_japan",
  1341: "alf04",
  1345: "fr610",
  1360: "fr210_japan",
  1380: "vector_ss",
  1381: "vector_cp",
  1386: "edge800_china",
  1387: "edge500_china",
  1405: "approach_g10",
  1410: "fr610_japan",
  1422: "edge500_korea",
  1436: "fr70",
  1446: "fr310xt_4t",
  1461: "amx",
  1482: "fr10",
  1497: "edge800_korea",
  1499: "swim",
  1537: "fr910xt_china",
  1551: "fenix",
  1555: "edge200_taiwan",
  1561: "edge510",
  1567: "edge810",
  1570: "tempe",
  1600: "fr910xt_japan",
  1619: "mt3333_1",
  1620: "mt3333_2",
  1621: "mt3333_3",
  1623: "fr620",
  1632: "fr220",
  1664: "fr910xt_korea",
  1688: "fr10_japan",
  1721: "edge810_japan",
  1735: "virb_elite",
  1736: "edge_touring",
  1742: "edge510_japan",
  1743: "hrm_tri",
  1752: "hrm_run",
  1765: "fr920xt",
  1821: "edge510_asia",
  1822: "edge810_china",
  1823: "edge810_taiwan",
  1836: "edge1000",
  1837: "vivo_fit",
  1853: "virb_remote",
  1885: "vivo_ki",
  1903: "fr15",
  1907: "vivo_active",
  1918: "edge510_korea",
  1928: "fr620_japan",
  1929: "fr620_china",
  1930: "fr220_japan",
  1931: "fr220_china",
  1936: "approach_s6",
  1956: "vivo_smart",
  1967: "fenix2",
  1988: "epix",
  2050: "fenix3",
  2052: "edge1000_taiwan",
  2053: "edge1000_japan",
  2061: "fr15_japan",
  2067: "edge520",
  2070: "edge1000_china",
  2072: "fr620_russia",
  2073: "fr220_russia",
  2079: "vector_s",
  2100: "edge1000_korea",
  2130: "fr920xt_taiwan",
  2131: "fr920xt_china",
  2132: "fr920xt_japan",
  2134: "virbx",
  2135: "vivo_smart_apac",
  2140: "etrex_touch",
  2147: "edge25",
  2148: "fr25",
  2150: "vivo_fit2",
  2153: "fr225",
  2156: "fr630",
  2157: "fr230",
  2158: "fr735xt",
  2160: "vivo_active_apac",
  2161: "vector_2",
  2162: "vector_2s",
  2172: "virbxe",
  2173: "fr620_taiwan",
  2174: "fr220_taiwan",
  2175: "truswing",
  2187: "d2airvenu",
  2188: "fenix3_china",
  2189: "fenix3_twn",
  2192: "varia_headlight",
  2193: "varia_taillight_old",
  2204: "edge_explore_1000",
  2219: "fr225_asia",
  2225: "varia_radar_taillight",
  2226: "varia_radar_display",
  2238: "edge20",
  2260: "edge520_asia",
  2261: "edge520_japan",
  2262: "d2_bravo",
  2266: "approach_s20",
  2271: "vivo_smart2",
  2274: "edge1000_thai",
  2276: "varia_remote",
  2288: "edge25_asia",
  2289: "edge25_jpn",
  2290: "edge20_asia",
  2292: "approach_x40",
  2293: "fenix3_japan",
  2294: "vivo_smart_emea",
  2310: "fr630_asia",
  2311: "fr630_jpn",
  2313: "fr230_jpn",
  2327: "hrm4_run",
  2332: "epix_japan",
  2337: "vivo_active_hr",
  2347: "vivo_smart_gps_hr",
  2348: "vivo_smart_hr",
  2361: "vivo_smart_hr_asia",
  2362: "vivo_smart_gps_hr_asia",
  2368: "vivo_move",
  2379: "varia_taillight",
  2396: "fr235_asia",
  2397: "fr235_japan",
  2398: "varia_vision",
  2406: "vivo_fit3",
  2407: "fenix3_korea",
  2408: "fenix3_sea",
  2413: "fenix3_hr",
  2417: "virb_ultra_30",
  2429: "index_smart_scale",
  2431: "fr235",
  2432: "fenix3_chronos",
  2441: "oregon7xx",
  2444: "rino7xx",
  2457: "epix_korea",
  2473: "fenix3_hr_chn",
  2474: "fenix3_hr_twn",
  2475: "fenix3_hr_jpn",
  2476: "fenix3_hr_sea",
  2477: "fenix3_hr_kor",
  2496: "nautix",
  2497: "vivo_active_hr_apac",
  2503: "fr35",
  2512: "oregon7xx_ww",
  2530: "edge_820",
  2531: "edge_explore_820",
  2533: "fr735xt_apac",
  2534: "fr735xt_japan",
  2544: "fenix5s",
  2547: "d2_bravo_titanium",
  2567: "varia_ut800",
  2593: "running_dynamics_pod",
  2599: "edge_820_china",
  2600: "edge_820_japan",
  2604: "fenix5x",
  2606: "vivo_fit_jr",
  2622: "vivo_smart3",
  2623: "vivo_sport",
  2628: "edge_820_taiwan",
  2629: "edge_820_korea",
  2630: "edge_820_sea",
  2650: "fr35_hebrew",
  2656: "approach_s60",
  2667: "fr35_apac",
  2668: "fr35_japan",
  2675: "fenix3_chronos_asia",
  2687: "virb_360",
  2691: "fr935",
  2697: "fenix5",
  2700: "vivoactive3",
  2713: "edge_1030",
  2727: "fr35_sea",
  2733: "fr235_china_nfc",
  2769: "foretrex_601_701",
  2772: "vivo_move_hr",
  2787: "vector_3",
  2796: "fenix5_asia",
  2797: "fenix5s_asia",
  2798: "fenix5x_asia",
  2806: "approach_z80",
  2814: "fr35_korea",
  2819: "d2charlie",
  2831: "vivo_smart3_apac",
  2832: "vivo_sport_apac",
  2833: "fr935_asia",
  2859: "descent",
  2878: "vivo_fit4",
  2886: "fr645",
  2888: "fr645m",
  2891: "fr30",
  2900: "fenix5s_plus",
  2909: "Edge_130",
  2924: "edge_1030_asia",
  2927: "vivosmart_4",
  2945: "vivo_move_hr_asia",
  2957: "mt3333_4",
  2962: "approach_x10",
  2977: "fr30_asia",
  2988: "vivoactive3m_w",
  3003: "fr645_asia",
  3004: "fr645m_asia",
  3011: "edge_explore",
  3028: "gpsmap66",
  3049: "approach_s10",
  3066: "vivoactive3m_l",
  3076: "fr245",
  3077: "fr245_music",
  3085: "approach_g80",
  3092: "edge_130_asia",
  3095: "edge_1030_bontrager",
  3107: "cxd5603gf",
  3110: "fenix5_plus",
  3111: "fenix5x_plus",
  3112: "edge_520_plus",
  3113: "fr945",
  3121: "edge_530",
  3122: "edge_830",
  3126: "instinct_esports",
  3134: "fenix5s_plus_apac",
  3135: "fenix5x_plus_apac",
  3142: "edge_520_plus_apac",
  3143: "descent_t1",
  3144: "fr235l_asia",
  3145: "fr245_asia",
  3163: "vivo_active3m_apac",
  3192: "gen3_bsm",
  3193: "gen3_bcm",
  3218: "vivo_smart4_asia",
  3224: "vivoactive4_small",
  3225: "vivoactive4_large",
  3226: "venu",
  3246: "marq_driver",
  3247: "marq_aviator",
  3248: "marq_captain",
  3249: "marq_commander",
  3250: "marq_expedition",
  3251: "marq_athlete",
  3258: "descent_mk2",
  3282: "fr45",
  3284: "gpsmap66i",
  3287: "fenix6S_sport",
  3288: "fenix6S",
  3289: "fenix6_sport",
  3290: "fenix6",
  3291: "fenix6x",
  3299: "hrm_dual",
  3300: "hrm_pro",
  3308: "vivo_move3_premium",
  3314: "approach_s40",
  3321: "fr245m_asia",
  3349: "edge_530_apac",
  3350: "edge_830_apac",
  3378: "vivo_move3",
  3387: "vivo_active4_small_asia",
  3388: "vivo_active4_large_asia",
  3389: "vivo_active4_oled_asia",
  3405: "swim2",
  3411: "mt3333_5",
  3420: "marq_driver_asia",
  3421: "marq_aviator_asia",
  3422: "vivo_move3_asia",
  3441: "fr945_asia",
  3446: "vivo_active3t_chn",
  3448: "marq_captain_asia",
  3449: "marq_commander_asia",
  3450: "marq_expedition_asia",
  3451: "marq_athlete_asia",
  3461: "index_smart_scale_2",
  3466: "instinct_solar",
  3469: "fr45_asia",
  3473: "vivoactive3_daimler",
  3498: "legacy_rey",
  3499: "legacy_darth_vader",
  3500: "legacy_captain_marvel",
  3501: "legacy_first_avenger",
  3512: "fenix6s_sport_asia",
  3513: "fenix6s_asia",
  3514: "fenix6_sport_asia",
  3515: "fenix6_asia",
  3516: "fenix6x_asia",
  3535: "legacy_captain_marvel_asia",
  3536: "legacy_first_avenger_asia",
  3537: "legacy_rey_asia",
  3538: "legacy_darth_vader_asia",
  3542: "descent_mk2s",
  3558: "edge_130_plus",
  3570: "edge_1030_plus",
  3578: "rally_200",
  3589: "fr745",
  3596: "venusq_music",
  3599: "venusq_music_v2",
  3600: "venusq",
  3615: "lily",
  3624: "marq_adventurer",
  3638: "enduro",
  3639: "swim2_apac",
  3648: "marq_adventurer_asia",
  3652: "fr945_lte",
  3702: "descent_mk2_asia",
  3703: "venu2",
  3704: "venu2s",
  3737: "venu_daimler_asia",
  3739: "marq_golfer",
  3740: "venu_daimler",
  3750: "mt3333_6",
  3794: "fr745_asia",
  3799: "cxd56xxxx_1",
  3808: "varia_rct715",
  3809: "lily_asia",
  3812: "edge_1030_plus_asia",
  3813: "edge_130_plus_asia",
  3823: "approach_s12",
  3837: "venusq_asia",
  3843: "edge_1040",
  3850: "marq_golfer_asia",
  3851: "venu2_plus",
  3865: "ag3335m",
  3866: "ag3335mn",
  3869: "fr55",
  3872: "enduro_asia",
  3888: "instinct_2",
  3889: "instinct_2s",
  3905: "fenix7s",
  3906: "fenix7",
  3907: "fenix7x",
  3908: "fenix7s_apac",
  3909: "fenix7_apac",
  3910: "fenix7x_apac",
  3930: "descent_mk2s_asia",
  3934: "approach_s42",
  3943: "epix_gen2",
  3944: "epix_gen2_apac",
  3949: "venu2s_asia",
  3950: "venu2_asia",
  3978: "fr945_lte_asia",
  3982: "vivo_move_sport",
  3983: "vivomove_trend",
  3986: "approach_S12_asia",
  3990: "fr255_music",
  3991: "fr255_small_music",
  3992: "fr255",
  3993: "fr255_small",
  4002: "approach_s42_asia",
  4005: "descent_g1",
  4017: "venu2_plus_asia",
  4024: "fr955",
  4033: "fr55_asia",
  4058: "cxd56xxxx_2",
  4061: "edge_540",
  4062: "edge_840",
  4063: "vivosmart_5",
  4071: "instinct_2_asia",
  4105: "marq_gen2",
  4115: "venusq2",
  4116: "venusq2music",
  4124: "marq_gen2_aviator",
  4125: "d2_air_x10",
  4130: "hrm_pro_plus",
  4132: "descent_g1_asia",
  4135: "tactix7",
  4155: "instinct_crossover",
  4169: "edge_explore2",
  4197: "cxd56xxxx_3",
  4222: "descent_mk3",
  4223: "descent_mk3i",
  4233: "approach_s70",
  4257: "fr265_large",
  4258: "fr265_small",
  4260: "venu3",
  4261: "venu3s",
  4265: "tacx_neo_smart",
  4266: "tacx_neo2_smart",
  4267: "tacx_neo2_t_smart",
  4268: "tacx_neo_smart_bike",
  4269: "tacx_satori_smart",
  4270: "tacx_flow_smart",
  4271: "tacx_vortex_smart",
  4272: "tacx_bushido_smart",
  4273: "tacx_genius_smart",
  4274: "tacx_flux_flux_s_smart",
  4275: "tacx_flux2_smart",
  4276: "tacx_magnum",
  4305: "edge_1040_asia",
  4312: "epix_gen2_pro_42",
  4313: "epix_gen2_pro_47",
  4314: "epix_gen2_pro_51",
  4315: "fr965",
  4341: "enduro2",
  4374: "fenix7s_pro_solar",
  4375: "fenix7_pro_solar",
  4376: "fenix7x_pro_solar",
  4380: "lily2",
  4394: "instinct_2x",
  4426: "vivoactive5",
  4432: "fr165",
  4433: "fr165_music",
  4440: "edge_1050",
  4442: "descent_t2",
  4446: "hrm_fit",
  4472: "marq_gen2_commander",
  4477: "lily_athlete",
  4532: "fenix8_solar",
  4533: "fenix8_solar_large",
  4534: "fenix8_small",
  4536: "fenix8",
  4556: "d2_mach1_pro",
  4565: "fr970",
  4575: "enduro3",
  4583: "instinctE_40mm",
  4584: "instinctE_45mm",
  4585: "instinct3_solar_45mm",
  4586: "instinct3_amoled_45mm",
  4587: "instinct3_amoled_50mm",
  4588: "descent_g2",
  4606: "hrm_200",
  4625: "vivoactive6",
  4647: "approachS44",
  4656: "approachS50",
  4666: "fenix_e",
  4759: "instinct3_solar_50mm",
  4775: "tactix8_amoled",
  4776: "tactix8_solar",
  10007: "sdm4",
  10014: "edge_remote",
  20119: "training_center",
  20533: "tacx_training_app_win",
  20534: "tacx_training_app_mac",
  20565: "tacx_training_app_mac_catalyst",
  30045: "tacx_training_app_android",
  30046: "tacx_training_app_ios",
  30047: "tacx_training_app_legacy",
  65531: "connectiq_simulator",
  65532: "android_antplus_plugin",
  65534: "connect",
  " 3927": "approach_g12",
  " 4001": "approach_g12_asia"
};
/* eslint-enable quote-props */

const fitSportToWaterspeed: Record<string | number, number> = {
  15: 13, // Rowing ➝ Rowing
  23: 6, // Boating ➝ Power boat
  32: 1, // Sailing ➝ Sailing
  37: 3, // Stand Up Paddleboarding ➝ SUP
  38: 8, // Surfing ➝ Surf
  39: 12, // Wakeboarding ➝ WakeboardParsing error: The `assert` keyword in import attributes is deprecated and it has been replaced by the `with` keyword. You can enable the `deprecatedImportAssert` parser plugin to suppress this error. (11:58)
  40: 11, // Water Skiing ➝ Waterskiing
  41: 5, // Kayaking ➝ Kayak
  43: 2, // Windsurfing ➝ Windsurf
  44: 4, // Kitesurfing ➝ Kite
  0: 0 // Generic ➝ Other
};

const existingWSSports = WATERSPORT_OPTIONS.map((sport) => sport.value.toString()).filter((opt) => opt !== "");

const sportNameToId = Object.entries(FIT.types.sport).reduce(
  (acc: Record<string, string>, [key, value]: [string, any]) => {
    acc[value] = key;
    return acc;
  },
  {}
);

export interface ParsedFitRecord {
  accuracy: number;
  calories: number;
  heartRate: number;
  latitude: number;
  longitude: number;
  bearing: number;
  pauseTime: number;
  speed: number | null;
  elevation: number;
  timestamp: number;
  distanceFromPreviousPoint: number;
}

/**
 * Read a FIT date field as epoch seconds.
 * @param {*} value A `Date` from the parser, or an already-numeric timestamp
 * @return {number|null} Epoch seconds, or null when unusable
 */
const toEpochSeconds = (value: any): number | null => {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms / 1000 : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
};

/**
 * Pick the FIT session that actually describes the recorded track.
 *
 * A file can hold several sessions, and the first one is not necessarily the ride.
 * Garmin watches in particular emit a "false start" session — the user taps start then
 * immediately stops, producing a session of a few seconds with no GPS fix — and the
 * next upload carries it ahead of the real session. Taking `sessions[0]` then reads the
 * activity's start date, duration, distance and speeds off that stub: `dS` lands hours
 * or days before the track, `sT` becomes a few seconds, and the missing `total_distance`
 * sends the distance down the record-derived fallback.
 *
 * The records are the ground truth, so choose the session whose time window covers most
 * of them, falling back to the longest session when no window matches (or there are no
 * positioned records to match against).
 * @param {Array} sessions `data.sessions` as returned by the parser
 * @param {Array} records The positioned records the track is built from
 * @return {object} The best matching session, or `{}` when there is none
 */
export const selectSessionForRecords = (sessions: any[], records: any[]): any => {
  if (!Array.isArray(sessions) || sessions.length === 0) return {};
  if (sessions.length === 1) return sessions[0] || {};

  const longestSession = () =>
    sessions.reduce(
      (best: any, candidate: any) =>
        (candidate?.total_elapsed_time || 0) > (best?.total_elapsed_time || 0) ? candidate : best,
      sessions[0]
    ) || {};

  const timestamps = (Array.isArray(records) ? records : [])
    .map((record: any) => toEpochSeconds(record?.timestamp))
    .filter((t: number | null): t is number => t !== null);
  if (timestamps.length === 0) return longestSession();

  let best: any = null;
  let bestCovered = -1;
  for (const candidate of sessions) {
    const start = toEpochSeconds(candidate?.start_time);
    if (start === null) continue;
    // Allow a second of slack at each edge: the session boundaries and the record
    // timestamps are rounded independently by the device.
    const end = start + (candidate.total_elapsed_time || 0);
    let covered = 0;
    for (const t of timestamps) {
      if (t >= start - 1 && t <= end + 1) covered++;
    }
    if (covered > bestCovered) {
      bestCovered = covered;
      best = candidate;
    }
  }

  return bestCovered > 0 && best ? best : longestSession();
};

/**
 * Rebuild an activity distance from its FIT records, in km.
 *
 * Some FIT sessions (seen on COROS watches for low-speed sessions) carry no
 * `total_distance`, so the distance has to be derived from the records themselves.
 * Sum the per-segment gaps: `distanceFromPreviousPoint` is a haversine distance in
 * metres, and the speed fallback needs the gap to the *previous* record — using the
 * elapsed time since the start of the activity instead makes the sum grow
 * quadratically with the record count.
 * @param {ParsedFitRecord[]} records The parsed FIT records, in chronological order
 * @return {number} The distance in km
 */
export const computeTotalDistanceFromRecords = (records: ParsedFitRecord[]): number => {
  if (!Array.isArray(records) || records.length < 2) {
    return 0;
  }

  let distanceInMeters = 0;
  for (let index = 1; index < records.length; index++) {
    const record = records[index];
    if (record.pauseTime !== 0) continue;

    const gapFromPreviousPoint = record.distanceFromPreviousPoint;
    if (typeof gapFromPreviousPoint === "number" && gapFromPreviousPoint > 0) {
      distanceInMeters += gapFromPreviousPoint;
      continue;
    }

    // No usable coordinates: fall back to speed (m/s) over the elapsed time since the
    // previous record.
    const elapsedSeconds = record.timestamp - records[index - 1].timestamp;
    if (elapsedSeconds > 0 && typeof record.speed === "number" && record.speed > 0) {
      distanceInMeters += record.speed * elapsedSeconds;
    }
  }

  // `totalDistance` is in km (the session path divides `total_distance` by 1000).
  return distanceInMeters / 1000;
};

export interface ParsedFitActivity {
  startTimestamp: number;
  sessionName: string;
  feeling: number | null;
  typeId: number;
  sportId: number | null;
  bearing: number | null;
  from: string;
  to: string;
  equipment1: string;
  equipment2: string;
  equipment3: string;
  city: string;
  country: string;
  pausedTime: number;
  windAngle: number | null;
  windDirection: number | null;
  windSpeed: number | null;
  totalTime: number;
  totalDistance: number;
  avgSpeed: number;
  maxSpeed: number;
  latitude: number;
  longitude: number;
  comment: string;
  calories: number;
  garminActivityId: string | null;
  suuntoWorkoutKey: string | null;
  corosWorkoutKey: string | null;
  deviceName: string | null;
  records: ParsedFitRecord[];
}

export const parseFitFile = (
  fitFileBuffer: ArrayBuffer | Buffer | Uint8Array,
  uid?: string,
  forcedWatersportId?: number
): Promise<ParsedFitActivity> => {
  return new Promise<ParsedFitActivity>((resolve, reject) => {
    const fitParser = new FitParser({
      force: true,
      speedUnit: "km/h",
      lengthUnit: "m",
      temperatureUnit: "celsius",
      elapsedRecordField: true,
      mode: "list"
    });

    fitParser.parse(fitFileBuffer, (error: any, data: any) => {
      if (error) {
        console.error(`Error parsing FIT file${uid ? ` for user ${uid}` : ""}:`, error);
        return reject(error);
      }

      let records = (data.records || []).filter((r: any) => !isNil(r.position_lat) && !isNil(r.position_long));
      const session = selectSessionForRecords(data.sessions, records);

      // Deduplicate records based on timestamp, latitude, and longitude
      // Keep the record with more data (e.g., with speed if available)
      const uniqueRecords: any[] = [];
      const seenKeys = new Set();

      records.forEach((record: any) => {
        const timestamp = record.timestamp ? record.timestamp.getTime() : 0;
        const key = `${timestamp}-${record.position_lat.toFixed(7)}-${record.position_long.toFixed(7)}`;

        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueRecords.push(record);
        } else {
          // Found duplicate - check if this one has more data (e.g., speed or enhanced_speed)
          const existingIndex = uniqueRecords.findIndex((r: any) => {
            const existingTs = r.timestamp ? r.timestamp.getTime() : 0;
            return (
              existingTs === timestamp &&
              Math.abs(r.position_lat - record.position_lat) < 0.0000001 &&
              Math.abs(r.position_long - record.position_long) < 0.0000001
            );
          });

          if (existingIndex >= 0) {
            const existing = uniqueRecords[existingIndex];
            // If new record has speed/enhanced_speed and existing doesn't, replace it
            const hasNewSpeed = !isNil(record.speed) || !isNil(record.enhanced_speed);
            const hasExistingSpeed = !isNil(existing.speed) || !isNil(existing.enhanced_speed);

            if (hasNewSpeed && !hasExistingSpeed) {
              uniqueRecords[existingIndex] = record;
            }
          }
        }
      });

      records = uniqueRecords;
      console.log(`Deduplicated FIT records: ${records.length} unique points from original`);

      let sportId: any;
      if (!isNil(forcedWatersportId)) {
        // Check custom CIQ fields (e.g. craft from APPro) before falling back to forced value,
        // since the craft field is more specific (e.g. "windfoil" vs generic "windsurfing")
        if (!isNil(session.craft)) {
          const craft = session.craft.toLowerCase?.() || session.craft;
          switch (craft) {
            case "windsurf":
              sportId = 2;
              break;
            case "windfoil":
              sportId = 10;
              break;
            case "kitesurf":
              sportId = 4;
              break;
            case "kitefoil":
              sportId = 14;
              break;
            case "wingfoil":
              sportId = 18;
              break;
          }
        }
        if (isNil(sportId)) {
          sportId = forcedWatersportId;
        }
      } else {
        if (!isNil(data.sport_id) && data.sport_id.toString() in existingWSSports) {
          // Check if this sport_id is supported
          sportId = data.sport_id;
        }
        if (isNil(sportId) && !isNil(session.sport_id) && session.sport_id.toString() in existingWSSports) {
          // Check if this sport_id is supported
          sportId = session.sport_id;
        }
        if (isNil(sportId) && !isNil(session.craft)) {
          const craft = session.craft.toLowerCase?.() || session.craft;
          // From APPro (https://apps.garmin.com/apps/9567700b-6587-44be-9708-879bfc844791), can be windsurf, windfoil, kitesurf, kitefoil, wingfoil
          switch (craft) {
            case "windsurf":
              sportId = 2; // Windsurf
              break;
            case "windfoil":
              sportId = 10; // Windfoil
              break;
            case "kitesurf":
              sportId = 4; // Kite
              break;
            case "kitefoil":
              sportId = 14; // Kitefoil
              break;
            case "wingfoil":
              sportId = 18; // Wingfoil
              break;
            default:
              if (!isNil(session.sport)) {
                sportId = mapFitSportToSportId(session.sport, uid); // Fallback to generic sport
              }
              break;
          }
        }

        // If not, try to map it to a supported sport
        if (isNil(sportId) && !isNil(session.sport)) {
          sportId = mapFitSportToSportId(session.sport, uid);
        }
        // Check if sportId is a string, if so, convert it to an integer
        if (typeof sportId === "string") {
          sportId = parseInt(sportId, 10);
        }
      }

      const parsed: ParsedFitActivity = {
        startTimestamp: session.start_time.getTime() / 1000,
        sessionName: "",
        feeling: null,
        typeId: 0,
        sportId: sportId,
        bearing: null,
        from: "",
        to: "",
        equipment1: "",
        equipment2: "",
        equipment3: "",
        city: "",
        country: "",
        pausedTime: session.total_elapsed_time - session.total_timer_time || 0,
        windAngle: null,
        windDirection: null,
        windSpeed: null,
        totalTime: session.total_elapsed_time || 0,
        totalDistance: session.total_distance ? session.total_distance / 1000 : 0,
        avgSpeed: session.avg_speed || session.enhanced_avg_speed || 0,
        maxSpeed: session.max_speed || session.enhanced_max_speed || 0,
        latitude: records[0]?.position_lat || 0,
        longitude: records[0]?.position_long || 0,
        comment: session.description || "",
        calories: session.total_calories || 0,
        garminActivityId: null,
        suuntoWorkoutKey: null,
        corosWorkoutKey: null,
        deviceName:
          data.device_infos.find((d: any) => d.product_name && d.product_name.length > 0)?.product_name ||
          deviceMapping[
            data.device_infos.find((d: any) => d.product && deviceMapping[d.product.toString()]?.length > 0)?.product
          ] ||
          null,
        records: records.map((r: any, index: number) => ({
          accuracy: r.gps_accuracy,
          calories: r.calories,
          heartRate: r.heart_rate,
          latitude: r.position_lat,
          longitude: r.position_long,
          bearing:
            index !== 0
              ? DirectionUtil.getBearingValue(
                  records[index - 1].position_lat,
                  records[index - 1].position_long,
                  r.position_lat,
                  r.position_long
                )
              : -1,
          pauseTime: 0,
          speed:
            !isNil(r.speed) || !isNil(r.enhanced_speed)
              ? ConvertUtil.convertSpeed(
                  !isNil(r.enhanced_speed) ? r.enhanced_speed : r.speed,
                  SETTING_SPEED_UNITS.m_slash_s
                )
              : null,
          elevation: r.altitude || r.enhanced_altitude,
          timestamp: r.timestamp.getTime() / 1000,
          distanceFromPreviousPoint:
            index !== 0
              ? CoordinateUtil.haversineDistance(
                  r.position_lat,
                  r.position_long,
                  records[index - 1].position_lat,
                  records[index - 1].position_long
                )
              : 0
        }))
      };

      // Apply speed interpolation and filtering using the utility function
      const fitPoints = parsed.records.map((record) => ({
        latitude: record.latitude,
        longitude: record.longitude,
        timestamp: record.timestamp,
        speed: record.speed || -1, // Use -1 to indicate missing speed
        heartRate: record.heartRate,
        elevation: record.elevation,
        accuracy: record.accuracy,
        calories: record.calories,
        bearing: record.bearing,
        pauseTime: record.pauseTime,
        distanceFromPreviousPoint: record.distanceFromPreviousPoint
      }));

      const processedFitPoints = interpolateAndFilterSpeedFromPoints(fitPoints);

      // Update the records with the processed speed values
      parsed.records = parsed.records.map((record, index) => ({
        ...record,
        speed: processedFitPoints[index].speed
      }));

      parsed.bearing =
        parsed.records.length > 0
          ? DirectionUtil.getBearingValue(
              parsed.records[0].latitude,
              parsed.records[0].longitude,
              parsed.records[parsed.records.length - 1].latitude,
              parsed.records[parsed.records.length - 1].longitude
            )
          : null;

      if (!parsed.totalDistance) {
        parsed.totalDistance = computeTotalDistanceFromRecords(parsed.records);
      }

      if (!parsed.avgSpeed) {
        parsed.avgSpeed = ConvertUtil.convertMPSToKMH(
          parsed.records.reduce((acc, record) => {
            if (record.pauseTime === 0) {
              return acc + record.speed;
            }
            return acc;
          }, 0) / parsed.records.length
        );
      }

      if (!parsed.maxSpeed) {
        parsed.maxSpeed = ConvertUtil.convertMPSToKMH(Math.max(...parsed.records.map((record) => record.speed)));
      }

      // Check if the last point have calories otherwise set it the total_calories from session
      if (parsed.records.length > 0 && isNil(parsed.records[parsed.records.length - 1].calories)) {
        parsed.records[parsed.records.length - 1].calories = parsed.calories;
      }

      resolve(parsed);
    });
  });
};

const mapFitSportToSportId = (fitSport: string, uid?: string): number | null => {
  const fitSportId = sportNameToId[fitSport];
  if (fitSportId.toString() in fitSportToWaterspeed) {
    return fitSportToWaterspeed[fitSportId];
  }

  console.warn(`FIT sport ID not supported or not found${uid ? ` for user ${uid}` : ""}:`, fitSport);
  return null;
};
